import React from 'react';
import { FiCheck } from 'react-icons/fi';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { MODALITY_LABEL, ETAPA_LABEL } from '@/components/custom/question/question-detail-content';

// Mesma heurística de QuestionRowTeacher.jsx / QuestionsList.jsx (default) pra
// separar, dentro de `taxonomies`, a matéria do descritor SAEB e dos sub-conteúdos.
const MATERIAS = new Set(['Matemática', 'Língua Portuguesa', 'Lingua Portuguesa']);
const SAEB_CODE = /^(D\d+|[259][A-Z]+\d+\.\d+|3[A-Z]+\d+\.\d+)/;

function Section({ title, children }) {
  return (
    <div className="mt-5">
      <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-neutral-600">{title}</h4>
      {children}
    </div>
  );
}

function ClassificationRow({ label, children }) {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-3 first:mt-0">
      <span className="min-w-[110px] text-xs text-neutral-600">{label}</span>
      {children}
    </div>
  );
}

export function QuestionDetailDialog({ question, onClose }) {
  if (!question) {
    return null;
  }

  const a = question.attributes || {};
  const taxs = a.taxonomies || [];
  const materia = taxs.find(t => MATERIAS.has(t.name))?.name;
  const descritorSaeb = taxs.find(t => SAEB_CODE.test(t.name))?.name;
  const subConteudo = taxs.filter(t => t.name !== materia && t.name !== descritorSaeb);
  const motivatorTexts = a['motivator-texts'] || [];
  const codigo = a['external-id'] || a.code || `#${question.id}`;
  const alternatives = a.alternatives || [];

  return (
    <Dialog
      open={Boolean(question)}
      onOpenChange={onClose}
    >
      <DialogContent
        layout="flex"
        className="max-h-[85vh] w-full overflow-y-auto sm:w-[600px]"
      >
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="info-soft">{MODALITY_LABEL[a.modality] || a.modality}</Badge>
            <Badge variant={a.published ? 'success-soft' : 'muted'}>{a.published ? 'Publicada' : 'Rascunho'}</Badge>
            <span className="ml-auto max-w-full truncate text-xs font-semibold text-neutral-600">{codigo}</span>
          </div>
          <p className="break-words text-xs font-semibold text-neutral-700">{[a.etapa && ETAPA_LABEL[a.etapa], materia, descritorSaeb].filter(Boolean).join(' · ')}</p>
          <DialogTitle className="sr-only">Detalhes da questão {codigo}</DialogTitle>
          <DialogDescription className="sr-only">Detalhes da questão {codigo}</DialogDescription>
        </DialogHeader>

        {motivatorTexts.length > 0 && (
          <Section title="Texto motivador">
            {motivatorTexts.map(t => (
              <div
                key={t.id}
                className="prose prose-sm max-w-none break-words rounded-lg bg-neutral-50 p-3 text-sm leading-relaxed text-neutral-700"
                dangerouslySetInnerHTML={{ __html: t.body }}
              />
            ))}
          </Section>
        )}

        <Section title="Enunciado da questão">
          <p className="border-[var(--color-mission)]/40 break-words border-l-2 bg-neutral-50 py-2 pl-3 pr-2 text-sm font-semibold leading-relaxed text-neutral-900">{a.description}</p>
        </Section>

        {alternatives.length > 0 && (
          <Section title="Alternativas">
            <ol className="space-y-2">
              {alternatives.map((alt, index) => {
                const correct = alt['is-correct'];
                return (
                  <li
                    key={alt.id}
                    data-correct={correct}
                    className={['flex items-start gap-3 rounded-lg border p-3', correct ? 'border-emerald-200 bg-emerald-50' : 'border-neutral-100 bg-white'].join(' ')}
                  >
                    <span className={['flex size-7 shrink-0 items-center justify-center rounded-md text-xs font-semibold', correct ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-50 text-neutral-700'].join(' ')}>
                      {String.fromCharCode(65 + index)}
                    </span>
                    <span
                      className="min-w-0 flex-1 break-words text-sm leading-relaxed text-neutral-700"
                      dangerouslySetInnerHTML={{ __html: alt.description || '' }}
                    />
                    {correct && (
                      <FiCheck
                        className="mt-1 h-4 w-4 shrink-0 text-emerald-600"
                        strokeWidth={2.5}
                      />
                    )}
                  </li>
                );
              })}
            </ol>
          </Section>
        )}

        {(materia || a.etapa || descritorSaeb || subConteudo.length > 0) && (
          <Section title="Classificação">
            {materia && (
              <ClassificationRow label="Matéria">
                <Badge variant="info-soft">{materia}</Badge>
              </ClassificationRow>
            )}
            {a.etapa && (
              <ClassificationRow label="Etapa">
                <Badge
                  variant="muted"
                  className="text-neutral-700"
                >
                  {ETAPA_LABEL[a.etapa] || a.etapa}
                </Badge>
              </ClassificationRow>
            )}
            {descritorSaeb && (
              <ClassificationRow label="Descritor SAEB">
                <Badge
                  variant="muted"
                  className="text-neutral-700"
                >
                  <span className="font-mono">{descritorSaeb}</span>
                </Badge>
              </ClassificationRow>
            )}
            {subConteudo.length > 0 && (
              <ClassificationRow label="Sub-conteúdos">
                {/* min-w-0: sem isso, um badge com nome muito longo (nowrap + truncate)
                    faz esse wrapper "flex-wrap" recusar encolher e estourar a largura
                    do diálogo, em vez de truncar o texto com reticências. */}
                <div className="flex min-w-0 flex-wrap gap-1.5">
                  {subConteudo.map(t => (
                    <Badge
                      key={t.id}
                      variant="muted"
                      className="max-w-[300px] text-neutral-700"
                      title={t.name}
                    >
                      {t.name}
                    </Badge>
                  ))}
                </div>
              </ClassificationRow>
            )}
          </Section>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default QuestionDetailDialog;
